import * as React from "react"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete, Box, Checkbox, Collapse, Divider, FormControl, FormControlLabel, FormLabel, IconButton, Radio, RadioGroup, Stack, Tooltip, Typography } from "@mui/material"
import Grid from "@mui/material/Grid2"
import { useTrackedUser } from "../../UserState"
import { serviceOrderChip, sort_custom_statuses, template_service_communication } from "../../../Utils/service"
import { MaterialUISwitchTextEmail } from "../../../Utils/MaterialUISwitch"
import { MuiTelInput } from "mui-tel-input"
import { formatPhoneNumber } from "../../../Utils"
import { findLocation, useLocations } from "../../ReactQuery/QueryLocations"

const available_statuses = (status_types, my_user) => {
    return sort_custom_statuses(my_user.client.service_order_statuses).filter((status) => status_types.includes(my_user.client.service_order_statuses[status].type)).reduce((obj, key) => {
        obj[key] = my_user.client.service_order_statuses[key];
        return obj;
    }, {});
}

const get_option = (serviceOrder, my_user, status, status_object, allow_same_status_selection, show_customer_communication) => {
    let disabled = false
    let tooltip = ""
    if (status === serviceOrder.status_name && status_object.type === serviceOrder.status_type && !allow_same_status_selection) {
        disabled = true
        tooltip = "This is the current status"
    }
    if (status_object.type === 'WORK_COMPLETE' && serviceOrder.actively_worked) {
        disabled = true
        tooltip = "Cannot change status to a Work Complete status while work is actively in progress"
    }
    const option =
        <Tooltip title={tooltip} arrow key={`${status}-${status_object.type}`}>
            <FormControlLabel
                value={status}
                disabled={disabled}
                control={<Radio />}
                label={serviceOrderChip(my_user, status, status_object.type, status_object.customer_communication && show_customer_communication)}
            />
        </Tooltip>
    return option
}

export default function ChangeServiceOrderStatusModal({
    serviceOrder,
    open,
    status_types,
    callback,
    title = "Change Status",
    allow_notes = true,
    allow_same_status_selection = false,
    allow_customer_communication = false,
    phones = [{ value: '8438306713', name: 'Austin C' }, { value: '8438306714', name: 'Austin D' }, { value: '8438306715', name: 'Austin E' }],
    emails = [{ value: '8438306713', name: 'Austin C' }, { value: '8438306714', name: 'Austin D' }, { value: '8438306715', name: 'Austin E' }],
    default_phone = '',
    default_email = '',
    default_customer_communication_type = 'text',
}) {
    const [my_user,] = useTrackedUser();
    const { data: locations, } = useLocations();
    const [status, setStatus] = React.useState(serviceOrder.status_name ?? null)
    const [notes, setNotes] = React.useState("")
    const [customerCommunication, setCustomerCommunication] = React.useState(false)
    const [customerCommunicationType, setCustomerCommunicationType] = React.useState('text')
    const [customerCommunicationPhone, setCustomerCommunicationPhone] = React.useState('')
    const [customerCommunicationEmail, setCustomerCommunicationEmail] = React.useState('')
    const [customerCommunicationMessage, setCustomerCommunicationMessage] = React.useState('')
    const statuses = available_statuses(status_types, my_user)
    if (Object.keys(statuses).length === 1 && status !== Object.keys(statuses)[0]) {
        setStatus(Object.keys(statuses)[0])
    } else if (Object.keys(statuses).length > 0 && !Object.keys(statuses).includes(status)) {
        if (Object.keys(statuses).includes(serviceOrder.status_name)) {
            setStatus(serviceOrder.status_name)
        } else if (status !== null) {
            setStatus(null)
        }
    }

    React.useEffect(() => {
        setStatus(null)
        setNotes("")
        setCustomerCommunication(false)
        setCustomerCommunicationType(default_customer_communication_type ?? 'text')
        setCustomerCommunicationMessage('')
        setCustomerCommunicationPhone(default_phone ?? '')
        setCustomerCommunicationEmail(default_email ?? '')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    React.useEffect(() => {
        const new_status = my_user.client.service_order_statuses[status]
        if (status) {
            setCustomerCommunication((customerCommunication || new_status.customer_communication) && allow_customer_communication)
            const service_order_location = findLocation(locations, serviceOrder.location_id);
            if (default_customer_communication_type === 'text') {
                setCustomerCommunicationMessage(template_service_communication(service_order_location, serviceOrder, new_status.customer_communication_text ?? '', status))
            } else {
                setCustomerCommunicationMessage(template_service_communication(service_order_location, serviceOrder, new_status.customer_communication_email ?? '', status))
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status])

    return (
        (<div>
            <Dialog
                open={open}
                onClose={() => callback(false)}
                maxWidth={'md'}
                fullWidth
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <DialogTitle>
                    <span style={{ "paddingRight": "24px" }}>{title}</span>
                    <IconButton
                        aria-label="close"
                        onClick={() => callback(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box>
                        <Grid container direction="column" spacing={2} sx={{ minWidth: '300px' }}>
                            <Grid size="grow">
                                <FormControl>
                                    <FormLabel id="radio-buttons-group-label">New Status</FormLabel>
                                    <RadioGroup
                                        aria-labelledby="radio-buttons-group-label"
                                        name="radio-buttons-group"
                                        value={status}
                                        onChange={(event) => setStatus(event.target.value)}
                                    >
                                        {Object.keys(statuses).map((status) =>
                                            get_option(serviceOrder, my_user, status, statuses[status], allow_same_status_selection, allow_customer_communication)
                                        )}
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            {allow_customer_communication &&
                                <Grid size="grow">
                                    <FormControlLabel
                                        control={<Checkbox />}
                                        label="Send customer communication"
                                        checked={customerCommunication}
                                        onChange={(event) => setCustomerCommunication(event.target.checked)}
                                        disabled={status === null}
                                    />
                                    <Collapse in={customerCommunication} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
                                        <Grid container alignItems={"center"} justifyContent="space-between" spacing={2}>
                                            <Grid size="auto">
                                                <Stack direction="row" component="label" alignItems="center" justifyContent="flex-begin">
                                                    <Typography sx={{ fontWeight: 'bold' }}>Text</Typography>
                                                    <MaterialUISwitchTextEmail
                                                        onChange={(e) => {
                                                            const service_order_location = findLocation(locations, serviceOrder.location_id);
                                                            const new_message = e.target.checked ? my_user.client.service_order_statuses[status].customer_communication_email : my_user.client.service_order_statuses[status].customer_communication_text
                                                            setCustomerCommunicationType(e.target.checked ? 'email' : 'text');
                                                            setCustomerCommunicationMessage(template_service_communication(service_order_location, serviceOrder, new_message ?? '', status))
                                                        }}
                                                        checked={customerCommunicationType === 'email'}
                                                    />
                                                    <Typography sx={{ fontWeight: 'bold' }}>Email</Typography>
                                                </Stack>
                                            </Grid>
                                            <Grid size="grow">
                                                {customerCommunicationType === 'text' &&
                                                    <Autocomplete
                                                        options={phones}
                                                        value={customerCommunicationPhone}
                                                        isOptionEqualToValue={(option, value) => option.value === value}
                                                        getOptionLabel={(option) => option.value ?? option}
                                                        onChange={(event, newValue) => {
                                                            setCustomerCommunicationPhone(newValue?.value ?? '');
                                                        }}
                                                        freeSolo
                                                        size="small"
                                                        sx={{
                                                            '& .MuiFilledInput-root': {
                                                                pt: 0,
                                                                pl: '12px'
                                                            },
                                                            '& .MuiFilledInput-root .MuiFilledInput-input': {
                                                                pt: '25px',
                                                                pr: '12px',
                                                                pb: '8px',
                                                                pl: '12px',
                                                            }
                                                        }}
                                                        renderInput={(params) => {
                                                            return (
                                                                <div ref={params.InputProps.ref}>
                                                                    <MuiTelInput
                                                                        {...params.inputProps}
                                                                        ref={undefined}
                                                                        inputRef={params.inputProps.ref}
                                                                        className=""
                                                                        margin="dense"
                                                                        id="phone"
                                                                        label="Phone"
                                                                        type="tel"
                                                                        fullWidth
                                                                        variant="filled"
                                                                        value={`+1 ${customerCommunicationPhone}`}
                                                                        onChange={(event, extra) => {
                                                                            if (extra.nationalNumber !== null && extra.nationalNumber !== undefined && extra.nationalNumber.length < 11) {
                                                                                setCustomerCommunicationPhone(extra.nationalNumber)
                                                                            }
                                                                        }}
                                                                        InputLabelProps={{ shrink: true }}
                                                                        onlyCountries={['US']}
                                                                        defaultCountry="US"
                                                                        forceCallingCode
                                                                        disableDropdown
                                                                    />
                                                                </div>
                                                            )
                                                        }}
                                                        renderOption={(props, option, { inputValue }) => {
                                                            const { key, ...optionProps } = props;

                                                            return (
                                                                <li key={key} {...optionProps}>
                                                                    <div>
                                                                        <span>
                                                                            {formatPhoneNumber(option.value)}
                                                                        </span>
                                                                        <br />
                                                                        <Typography sx={{ opacity: '50%', fontSize: '.7rem', pl: 1 }}>{option.name}</Typography>
                                                                    </div>
                                                                </li>
                                                            );
                                                        }}
                                                    />
                                                }
                                                {customerCommunicationType === 'email' &&
                                                    <Autocomplete
                                                        options={emails}
                                                        value={customerCommunicationEmail}
                                                        isOptionEqualToValue={(option, value) => option.value === value}
                                                        getOptionLabel={(option) => option.value ?? option}
                                                        onChange={(event, newValue) => {
                                                            setCustomerCommunicationEmail(newValue?.value ?? '');
                                                        }}
                                                        onInputChange={(event, newInputValue) => {
                                                            setCustomerCommunicationEmail(newInputValue ?? '');
                                                        }}
                                                        freeSolo
                                                        renderInput={(params) => {
                                                            return (<TextField {...params} label="Email" variant="filled" fullWidth autoComplete='off' />)
                                                        }}
                                                        renderOption={(props, option, { inputValue }) => {
                                                            const { key, ...optionProps } = props;

                                                            return (
                                                                <li key={option.value} {...optionProps}>
                                                                    <div>
                                                                        <span>
                                                                            {option.value}
                                                                        </span>
                                                                        <br />
                                                                        <Typography sx={{ opacity: '50%', fontSize: '.7rem', pl: 1 }}>{option.name}</Typography>
                                                                    </div>
                                                                </li>
                                                            );
                                                        }}
                                                    />
                                                }
                                            </Grid>
                                            <Grid size={12}>
                                                <TextField
                                                    margin="dense"
                                                    id="customercommunicationmessage"
                                                    label="Message to Customer"
                                                    type="text"
                                                    fullWidth
                                                    variant="filled"
                                                    multiline
                                                    minRows={2}
                                                    value={customerCommunicationMessage}
                                                    onChange={(event) => setCustomerCommunicationMessage(event.target.value)}
                                                    sx={{ mt: 0 }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Collapse>
                                </Grid>
                            }
                            {allow_notes &&
                                <React.Fragment>
                                    <Divider />
                                    <Grid size="grow">
                                        <TextField
                                            margin="dense"
                                            id="changestatusnote"
                                            label="Notes (optional)"
                                            type="text"
                                            fullWidth
                                            variant="filled"
                                            multiline
                                            minRows={2}
                                            value={notes}
                                            onChange={(event) => setNotes(event.target.value)}
                                            sx={{ mt: 0 }}
                                            slotProps={{
                                                input: { maxLength: 2055 }
                                            }}
                                        />
                                    </Grid>
                                </React.Fragment>
                            }
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Grid container justifyContent="space-between" sx={{ padding: "0px 30px 10px 30px", width: '100%' }}>
                        <Grid size="auto">
                        </Grid>
                        <Grid size="auto">
                            <Button variant="contained" autoFocus
                                onClick={() => {
                                    let customerCommunicationObject = undefined
                                    if (customerCommunication) {
                                        customerCommunicationObject = {
                                            destination_type: customerCommunicationType,
                                            destination: customerCommunicationType === 'text' ? customerCommunicationPhone : customerCommunicationEmail,
                                            message: customerCommunicationMessage
                                        }
                                    }
                                    callback(true, status, notes, customerCommunicationObject)
                                }}
                                disabled={status === null}
                            >
                                Update
                            </Button>
                        </Grid>
                    </Grid>
                </DialogActions>
            </Dialog>
        </div>)
    );
}
